// Abstracción de almacenamiento que funciona en Web y React Native

export interface StorageClient {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
  getAllKeys(): Promise<string[]>;
}

// Storage keys constants
export const STORAGE_KEYS = {
  AUTH_TOKEN: '@yawi/auth_token',
  USER_DATA: '@yawi/user_data',
  THEME: '@yawi/theme',
  LANGUAGE: '@yawi/language',
  NOTIFICATION_SETTINGS: '@yawi/notification_settings',
  LAST_SYNC: '@yawi/last_sync',
  OFFLINE_QUEUE: '@yawi/offline_queue',
  CACHE_HOME: '@yawi/cache_home',
  CACHE_ACCESS: '@yawi/cache_access',
  CACHE_GUESTS: '@yawi/cache_guests',
  CACHE_AMENITIES: '@yawi/cache_amenities',
  DATA_SAVING: '@yawi/data_saving',
} as const;

// Web implementation using localStorage. Envuelto en try/catch: algunos
// navegadores lanzan un SecurityError síncrono al acceder a localStorage
// cuando el usuario bloquea todo el storage del sitio (no solo cookies) —
// sin esto, cualquier llamada de apiClient.ts que pase por acá (setTokens,
// setCurrentTenant, etc.) tira una excepción no capturada.
export const createWebStorageClient = (): StorageClient => ({
  getItem: async (key: string) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage bloqueado: seguimos sin persistencia en vez de crashear.
    }
  },
  removeItem: async (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignorar
    }
  },
  clear: async () => {
    try {
      // Only clear yawi keys
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith('@yawi/')) {
          localStorage.removeItem(key);
        }
      });
    } catch {
      // Ignorar
    }
  },
  getAllKeys: async () => {
    try {
      return Object.keys(localStorage).filter((key) => key.startsWith('@yawi/'));
    } catch {
      return [];
    }
  },
});

// Mobile implementation using SecureStore (React Native/Expo)
// Esta función debe ser llamada desde el código de la app móvil
export const createMobileStorageClient = (secureStore: {
  getItemAsync: (key: string) => Promise<string | null>;
  setItemAsync: (key: string, value: string) => Promise<void>;
  deleteItemAsync: (key: string) => Promise<void>;
}): StorageClient => ({
  getItem: async (key: string) => {
    try {
      return await secureStore.getItemAsync(key);
    } catch {
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      await secureStore.setItemAsync(key, value);
    } catch (error) {
      console.warn('Failed to save to secure storage:', error);
    }
  },
  removeItem: async (key: string) => {
    try {
      await secureStore.deleteItemAsync(key);
    } catch (error) {
      console.warn('Failed to remove from secure storage:', error);
    }
  },
  clear: async () => {
    const knownKeys = Object.values(STORAGE_KEYS);
    for (const key of knownKeys) {
      try {
        await secureStore.deleteItemAsync(key);
      } catch {
        // Ignore errors
      }
    }
  },
  getAllKeys: async () => {
    // SecureStore no tiene getAllKeys, retornar las keys conocidas
    return Object.values(STORAGE_KEYS);
  },
});

// Helper to store JSON objects
export const createJsonStorage = <T>(storage: StorageClient, key: string) => ({
  get: async (): Promise<T | null> => {
    const value = await storage.getItem(key);
    if (!value) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  },
  set: async (value: T): Promise<void> => {
    await storage.setItem(key, JSON.stringify(value));
  },
  remove: async (): Promise<void> => {
    await storage.removeItem(key);
  },
});
